import { Anchor } from '@any-ui-react/core';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { ExportImportStatus } from '@/common/graphql';
import { EMPTY_DEFAULT } from '../../table';
import { Cell, CellValueProgress, HistoryList, type HistoryListHandle } from '../components';
import { EMPLOYEE_EXPORT_FORMAT_LABELS, PERFORMANCE_REVIEW_EXPORT_TYPE_LABELS } from './const';
import type { ExportHistoryKind } from './types';
import { useExportHistory } from './useExportHistory';

interface Props {
  modalId?: string;
  kind: ExportHistoryKind;
  onClose?: () => void;
}

export const ExportHistoryList = forwardRef<HistoryListHandle, Props>(({ modalId, kind, onClose }, ref) => {
  const [onlyMyTask, setOnlyMyTask] = useState(true);
  const { loading, rows, refetch } = useExportHistory(kind, onlyMyTask);

  useImperativeHandle(ref, () => ({ refetch }), [refetch]);

  const gridCols = useMemo(() => {
    switch (kind) {
      case 'employee':
      case 'performanceReviewList':
        return 'grid-cols-[200px_auto_100px_1fr]';
      default:
        return 'grid-cols-[200px_100px_1fr]';
    }
  }, [kind]);

  const gridColSpanWhenEmpty = useMemo(() => {
    switch (kind) {
      case 'employee':
      case 'performanceReviewList':
        return 'col-span-4';
      default:
        return 'col-span-3';
    }
  }, [kind]);

  return (
    <HistoryList
      ref={ref}
      classNames={{
        gridCols,
        gridColSpanWhenEmpty,
      }}
      columns={{
        secondHeader: headerClassNames => (
          <>
            {kind === 'employee' && <div className={classNames(headerClassNames, 'px-2')}>Format</div>}
            {kind === 'performanceReviewList' && <div className={classNames(headerClassNames, 'px-2')}>Type</div>}
          </>
        ),
        secondData: row => (
          <>
            {kind === 'employee' && (
              <Cell className="px-2 items-center justify-start text-xs whitespace-nowrap">
                {row.format ? EMPLOYEE_EXPORT_FORMAT_LABELS[row.format] : EMPTY_DEFAULT}
              </Cell>
            )}
            {kind === 'performanceReviewList' && (
              <Cell className="px-2 items-center justify-start text-xs whitespace-nowrap">
                {row.exportType ? PERFORMANCE_REVIEW_EXPORT_TYPE_LABELS[row.exportType] : EMPTY_DEFAULT}
              </Cell>
            )}
          </>
        ),
        resultData: row =>
          row.status === ExportImportStatus.Done && row.resultUrl ? (
            <Anchor
              href={row.resultUrl}
              className="text-xs break-all"
              rel="noreferrer"
              target="_blank"
              title={row.resultFileName ?? undefined}
            >
              {row.resultFileName ?? 'Google Sheets'}
            </Anchor>
          ) : row.status === ExportImportStatus.Failed ? (
            <div className="text-xs">{row.failureReason}</div>
          ) : (
            <CellValueProgress
              totalRequestCount={row.totalRequestCount}
              completedRequestCount={row.completedRequestCount}
            />
          ),
      }}
      emptyMessage="Exports you start will appear here."
      loading={loading}
      modalId={modalId}
      rows={rows}
      refetch={refetch}
      onChangeOnlyMyTask={setOnlyMyTask}
      onClose={onClose}
    />
  );
});

ExportHistoryList.displayName = 'ExportHistoryList';
